import { RSAA, RSAAAction } from 'redux-api-middleware';

import { RootState } from 'redux/rootReducer';
import { Dispatch } from 'redux';
import { TGetRootState } from 'types/shared';
import { EntityTypes } from 'context/entityContext';

import getBrowseCustomEntityListUrl from 'url-constructors/getBrowseCustomEntityListUrl';
import { IBrowseCustomListEntityData } from 'pages/CustomList/shared';

type OptionsType = {
  listId: string;
  countryIds: string | string[];
  customerFilter: string;
  region: string | string[];
  media: string;
  customRegion: string;
  limit: number;
  offset: number;
  nameFilter: string;
};

const REQUEST_LIST_ID = 'opus/playlistActivity/REQUEST_LIST_ID';
const GET_LIST_ID_SUCCESS = 'opus/playlistActivity/GET_LIST_ID_SUCCESS';
const FAILURE = 'opus/playlistActivitys/FAILURE';

const REQUEST_ADDED_ENTITIES = 'opus/playlistActivity/REQUEST_ADDED_ENTITIES';
const GET_ADDED_ENTITIES_SUCCESS =
  'opus/playlistActivity/GET_ADDED_ENTITIES_SUCCESS';
const FAILURE_ADDED_ENTITIES = 'opus/playlistActivity/FAILURE_ADDED_ENTITIES';

const REQUEST_ENTITIES = 'opus/playlistActivity/REQUEST_ENTITIES';
const GET_ENTITIES_SUCCESS = 'opus/playlistActivity/GET_ENTITIES_SUCCESS';
const FAILURE_ENTITIES = 'opus/playlistActivity/FAILURE_ENTITIES';

const REQUEST_DELETE = 'opus/playlistActivity/REQUEST_DELETE';
const DELETE_SUCCESS = 'opus/playlistActivity/DELETE_SUCCESS';
const FAILURE_DELETE = 'opus/playlistActivity/FAILURE_DELETE';

const REQUEST_UPDATE = 'opus/playlistActivity/REQUEST_UPDATE';
const UPDATE_SUCCESS = 'opus/playlistActivity/UPDATE_SUCCESS';
const FAILURE_UPDATE = 'opus/playlistActivity/FAILURE_UPDATE';

const REQUEST_CREATE_API_LIST = 'opus/playlistActivity/REQUEST_CREATE_API_LIST';
const CREATE_API_LIST_SUCCESS = 'opus/playlistActivity/CREATE_API_LIST_SUCCESS';
const FAILURE_CREATE_API_LIST = 'opus/playlistActivity/FAILURE_CREATE_API_LIST';

const REQUEST_CREATE_KV_LIST = 'opus/playlistActivity/REQUEST_CREATE_KV_LIST';
const CREATE_KV_LIST_SUCCESS = 'opus/playlistActivity/CREATE_KV_LIST_SUCCESS';
const FAILURE_CREATE_KV_LIST = 'opus/playlistActivity/FAILURE_CREATE_KV_LIST';

export interface IPlaylistActivityState {
  listIdData: {
    listId?: string | null;
    loading: boolean;
    success?: boolean;
    error?: undefined | Error;
  };
  customListEntitiesData: {
    entities: any[] | null;
    loading: boolean;
    success?: boolean;
    error?: undefined | Error;
  };
  addedEntitiesData: {
    addedEntities: any[] | null;
    loading: boolean;
    success?: boolean;
    error?: undefined | Error;
  };
  deletedEntitiesData: {
    loading: boolean;
    success?: boolean;
    error?: undefined | Error;
  };
}

const initialState: IPlaylistActivityState = {
  listIdData: {
    listId: null,
    loading: false,
    success: false,
    error: undefined,
  },
  customListEntitiesData: {
    entities: null,
    loading: false,
    success: false,
    error: undefined,
  },
  addedEntitiesData: {
    addedEntities: null,
    loading: false,
    success: false,
    error: undefined,
  },
  deletedEntitiesData: {
    loading: false,
    success: false,
    error: undefined,
  },
};

type PLaylistActivityAction =
  | {
      type:
        | typeof REQUEST_LIST_ID
        | typeof REQUEST_ADDED_ENTITIES
        | typeof REQUEST_ENTITIES
        | typeof REQUEST_DELETE
        | typeof REQUEST_UPDATE
        | typeof REQUEST_CREATE_API_LIST
        | typeof REQUEST_CREATE_KV_LIST;
    }
  | {
      type: typeof GET_LIST_ID_SUCCESS;
      payload: { listId: string };
    }
  | {
      type: typeof GET_ADDED_ENTITIES_SUCCESS;
      payload: {
        createdAt: Date | number;
        createdBy: string;
        displayName: string;
        entities: { id: string; type: EntityTypes }[];
        id: string;
        lastUpdatedAt: Date | number;
        lastUpdatedBy: string;
        templateUrl: null | string;
        thumbnailUrl: null | string;
        totalEntityCount: number;
      };
    }
  | {
      type: typeof GET_ENTITIES_SUCCESS;
      payload: {
        createdAt: Date | number;
        createdBy: string;
        displayName: string;
        entities: {
          data: IBrowseCustomListEntityData;
          entity: {
            displayName: string;
            group_child_count: number;
            id: string;
            is_group: boolean;
            isrc_list: string[];
            primaryArtist: {
              id: string;
              type: EntityTypes;
              displayName: string;
            };
            releaseDate: Date | number;
            thumbnailUrl: string;
            type: EntityTypes;
          };
        }[];
        id: string;
        lastUpdatedAt: Date | number;
        lastUpdatedBy: string;
        totalEntityCount: number;
      };
    }
  | {
      type: typeof CREATE_API_LIST_SUCCESS;
      payload: {
        listId: string;
        message: string;
      };
    }
  | {
      type: typeof CREATE_KV_LIST_SUCCESS;
      payload: {
        listId: string;
      };
    }
  | {
      type: typeof DELETE_SUCCESS;
      payload: {
        listId: string;
        message: string;
      };
    }
  | {
      type:
        | typeof FAILURE
        | typeof FAILURE_ADDED_ENTITIES
        | typeof FAILURE_ENTITIES
        | typeof FAILURE_DELETE
        | typeof FAILURE_UPDATE;
      payload: {
        message: string;
        name: string;
        response: any;
        status: number;
        statusText: string;
      };
    };

export default function reducer(
  state: IPlaylistActivityState = initialState,
  action: PLaylistActivityAction
): IPlaylistActivityState {
  switch (action.type) {
    case REQUEST_LIST_ID:
      return {
        ...state,
        listIdData: {
          loading: true,
        },
      };
    case GET_LIST_ID_SUCCESS: {
      return {
        ...state,
        listIdData: {
          listId: action?.payload.listId,
          loading: false,
          success: true,
          error: undefined,
        },
      };
    }
    case FAILURE:
      return {
        ...state,
        listIdData: {
          loading: false,
          success: false,
          error: new Error(action.payload.message),
        },
      };
    case REQUEST_ADDED_ENTITIES:
      return {
        ...state,
        addedEntitiesData: {
          loading: true,
          addedEntities: state.addedEntitiesData?.addedEntities,
        },
      };
    case GET_ADDED_ENTITIES_SUCCESS: {
      return {
        ...state,
        addedEntitiesData: {
          addedEntities: action.payload?.entities,
          loading: false,
          success: true,
          error: undefined,
        },
      };
    }
    case FAILURE_ADDED_ENTITIES:
      return {
        ...state,
        addedEntitiesData: {
          addedEntities: null,
          loading: false,
          success: false,
          error: new Error(action.payload.message),
        },
      };
    case REQUEST_ENTITIES:
      return {
        ...state,
        customListEntitiesData: {
          loading: true,
          entities: state.customListEntitiesData?.entities,
        },
      };
    case GET_ENTITIES_SUCCESS: {
      return {
        ...state,
        customListEntitiesData: {
          entities: action.payload?.entities,
          loading: false,
          success: true,
          error: undefined,
        },
      };
    }
    case FAILURE_ENTITIES:
    case FAILURE_DELETE:
    case FAILURE_UPDATE:
      return {
        ...state,
        customListEntitiesData: {
          entities: null,
          loading: false,
          success: false,
          error: new Error(action.payload.message),
        },
      };
    case REQUEST_DELETE:
      return {
        ...state,
        deletedEntitiesData: {
          loading: true,
        },
      };
    case DELETE_SUCCESS:
      return {
        ...state,
        deletedEntitiesData: {
          loading: false,
          success: true,
        },
        addedEntitiesData: {
          loading: true,
          addedEntities: state.addedEntitiesData?.addedEntities,
        },
      };
    case REQUEST_UPDATE:
      return {
        ...state,
        customListEntitiesData: {
          loading: true,
          entities: state.customListEntitiesData?.entities,
        },
      };
    case CREATE_API_LIST_SUCCESS:
      return {
        ...state,
        listIdData: {
          listId: action?.payload.listId,
          loading: false,
          success: true,
          error: undefined,
        },
      };

    default:
      return state;
  }
}

export function loadKVCustomListId(userId: string) {
  return async function (dispatch: Dispatch): Promise<any> {
    const response = await dispatch({
      [RSAA]: {
        types: [
          REQUEST_LIST_ID,
          {
            type: GET_LIST_ID_SUCCESS,
            payload: async function payload(
              _action: RSAAAction,
              _state: RootState,
              res: any
            ): Promise<any> {
              return res.json();
            },
          },
          FAILURE,
        ],
        endpoint: `/kv/${userId}_tracks_im_watching`,
        headers: { 'Content-Type': 'application/json' },
        method: 'GET',
        credentials: 'same-origin',
      },
    });

    return response;
  };
}

export function loadTracksIds(loadTracks: Function, options: OptionsType) {
  return async function (dispatch: Dispatch): Promise<any> {
    const response = await dispatch({
      [RSAA]: {
        types: [
          REQUEST_ADDED_ENTITIES,
          {
            type: GET_ADDED_ENTITIES_SUCCESS,
            payload: async function payload(
              _action: RSAAAction,
              _state: RootState,
              res: any
            ): Promise<any> {
              return res.json();
            },
          },
          FAILURE_ADDED_ENTITIES,
        ],
        endpoint: `/api/customEntityLists/${options?.listId}`,
        headers: { 'Content-Type': 'application/json' },
        method: 'GET',
        credentials: 'same-origin',
      },
    }).then((resp): any => {
      if (
        loadTracks &&
        resp?.payload?.entities &&
        resp.payload.entities.length > 0
      ) {
        return dispatch(loadTracks(options));
      }
    });

    return response;
  };
}

export function loadTracks(options: OptionsType) {
  return async function (dispatch: Dispatch): Promise<any> {
    const response = await dispatch({
      [RSAA]: {
        types: [
          REQUEST_ENTITIES,
          {
            type: GET_ENTITIES_SUCCESS,
            payload: async function payload(
              _action: RSAAAction,
              _state: RootState,
              res: any
            ): Promise<any> {
              return res.json();
            },
          },
          FAILURE_ENTITIES,
        ],
        endpoint: getBrowseCustomEntityListUrl(options),
        headers: { 'Content-Type': 'application/json' },
        method: 'GET',
        credentials: 'same-origin',
      },
    });

    return response;
  };
}

export function deleteEntity(
  entity: [{ id: string; type: 'track' }],
  loadTracksIds: Function,
  options: OptionsType,
  loadTracks: Function
) {
  return async function (dispatch: Dispatch): Promise<any> {
    const response = await dispatch({
      [RSAA]: {
        types: [
          REQUEST_DELETE,
          {
            type: DELETE_SUCCESS,
            payload: async function payload(
              _action: RSAAAction,
              _state: RootState,
              res: any
            ): Promise<any> {
              return res.json();
            },
          },
          FAILURE_DELETE,
        ],
        endpoint: `/api/customEntityLists/${options?.listId}/entities`,
        headers: { 'Content-Type': 'application/json' },
        method: 'DELETE',
        credentials: 'same-origin',
        body: JSON.stringify(entity),
      },
    }).then((): any => {
      if (loadTracksIds) {
        return dispatch(loadTracksIds(loadTracks, options));
      }
    });

    return response;
  };
}

export function updateAPICustomList(
  entityDetails: {
    tracks: [{ id: string; type: 'track' }];
    listId: string;
  },
  loadTracksIds: Function,
  options: OptionsType,
  loadTracks: Function
) {
  return async function (dispatch: Dispatch): Promise<any> {
    const response = await dispatch({
      [RSAA]: {
        types: [
          REQUEST_UPDATE,
          {
            type: UPDATE_SUCCESS,
            payload: async function payload(
              _action: RSAAAction,
              _state: RootState,
              res: any
            ): Promise<any> {
              return res.json();
            },
          },
          FAILURE_UPDATE,
        ],
        endpoint: `/api/customEntityLists/${options?.listId}/entities`,
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        credentials: 'same-origin',
        body: JSON.stringify(entityDetails?.tracks),
      },
    }).then((): any => {
      if (loadTracksIds) {
        return dispatch(loadTracksIds(loadTracks, options));
      }
    });

    return response;
  };
}

export function createAPICustomList(
  list: {
    displayName: string;
    entities: [{ id: string; type: 'track' }];
    hidden: boolean;
  },
  saveCustomListInKV: Function
) {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    const response = await dispatch({
      [RSAA]: {
        types: [
          REQUEST_CREATE_API_LIST,
          {
            type: CREATE_API_LIST_SUCCESS,
            payload: async function payload(
              _action: RSAAAction,
              _state: RootState,
              res: any
            ): Promise<any> {
              return res.json();
            },
          },
          FAILURE_CREATE_API_LIST,
        ],
        endpoint: '/api/customEntityLists',
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        credentials: 'same-origin',
        body: JSON.stringify(list),
      },
    }).then((resp: any): any => {
      const { authentication } = getState();
      const userId = authentication?.profile?.id;
      if (saveCustomListInKV) {
        return dispatch(
          saveCustomListInKV(resp?.payload?.listId, `user_${userId}`)
        );
      }
    });

    return response;
  };
}

export function saveCustomListInKV(listId: string, userId: string) {
  return async function (dispatch: Dispatch): Promise<any> {
    const response = await dispatch({
      [RSAA]: {
        types: [
          REQUEST_CREATE_KV_LIST,
          {
            type: CREATE_KV_LIST_SUCCESS,
            payload: async function payload(
              _action: RSAAAction,
              _state: RootState,
              res: any
            ): Promise<any> {
              return res.json();
            },
          },
          FAILURE_CREATE_KV_LIST,
        ],
        endpoint: `/kv/${userId}_tracks_im_watching`,
        headers: { 'Content-Type': 'application/json' },
        method: 'PUT',
        credentials: 'same-origin',
        body: JSON.stringify({ listId: listId }),
      },
    });

    return response;
  };
}
